// Capa de red compartida por el query builder, el cliente de auth y el de
// storage. Centraliza la URL base, la cabecera Authorization y el formato de
// error para que el resto del cliente no repita esa lógica.

const RAW_BASE = (import.meta as any)?.env?.VITE_API_BASE_URL ?? '/api';

export const API_BASE = String(RAW_BASE).replace(/\/$/, '');

export interface GatewayFailure {
  message: string;
  code?: string;
  details?: string | null;
  hint?: string | null;
  status?: number;
}

export class GatewayError extends Error {
  code: string;
  details: string | null;
  hint: string | null;
  status: number;

  constructor(failure: GatewayFailure, status: number) {
    super(failure.message || 'Error de red');
    this.name = 'GatewayError';
    this.code = failure.code ?? String(status);
    this.details = failure.details ?? null;
    this.hint = failure.hint ?? null;
    this.status = failure.status ?? status;
  }
}

/** Proveedor del access token; lo instala el cliente de auth al construirse. */
let tokenProvider: () => Promise<string | null> = async () => null;

export function setTokenProvider(provider: () => Promise<string | null>) {
  tokenProvider = provider;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Si es false no se adjunta el token (login, registro, recuperación). */
  auth?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export async function request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'POST', body, auth = true, signal, headers = {} } = options;

  const finalHeaders: Record<string, string> = {
    'content-type': 'application/json',
    ...headers,
  };

  if (auth) {
    const token = await tokenProvider();
    if (token) finalHeaders.authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: finalHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
      credentials: 'same-origin',
    });
  } catch (err: any) {
    if (err?.name === 'AbortError') throw err;
    throw new GatewayError(
      { message: 'No se pudo conectar con el servidor. Revisa tu conexión.', code: 'network_error' },
      0,
    );
  }

  const text = await response.text();
  let payload: any = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const failure: GatewayFailure = payload?.error ?? {
      message: text?.slice(0, 300) || `El servidor respondió ${response.status}`,
      code: String(response.status),
    };
    throw new GatewayError(failure, response.status);
  }

  return payload as T;
}
